
import type { Database } from "@/types/supabase-types"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Clock, Edit, Trash2, X, Calendar } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useState } from "react"
import { formatDayGroupsForDisplay } from "@/utils/shiftFormUtils"
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';
import { StatusBadge } from "@/utils/statusUtils";

type ShiftRow = Database['ShiftSage']['Tables']['shifts']['Row']

interface ShiftsListProps {
  shifts: ShiftRow[]
  onEditShift: (shift: ShiftRow) => void
  onRemoveShift: (shiftId: string) => void
  onDeleteShift: (shiftId: string) => void
}

export function ShiftsList({ shifts, onEditShift, onRemoveShift, onDeleteShift }: ShiftsListProps) {
  const [shiftToRemove, setShiftToRemove] = useState<string | null>(null)
  const [shiftToDelete, setShiftToDelete] = useState<string | null>(null)
  
  // Helper function to format shift schedule for display
  const formatShiftSchedule = (dayTimeDeduction: Database['ShiftSage']['Tables']['shifts']['Row']['day_time_deduction']): string[] => {
    if (!dayTimeDeduction || !Array.isArray(dayTimeDeduction) || dayTimeDeduction.length === 0) {
      return ['No schedule configured']
    }
    
    return formatDayGroupsForDisplay(dayTimeDeduction)
  }
  
  if (shifts.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground text-lg mb-2">No shifts configured yet</p>
        <p className="text-muted-foreground text-sm">Click 'Add Shift' to create your first work shift</p>
      </div>
    )
  }
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">Shift Name</TableHead>
            <TableHead className="font-semibold">Code</TableHead>
            <TableHead className="font-semibold">Schedule</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="font-semibold text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shifts.map((shift) => (
            <TableRow key={shift.id} className="hover:bg-muted/30 transition-colors">
              <TableCell className="font-medium">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                  {shift.name}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">{shift.code || '-'}</TableCell>
              <TableCell>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col gap-1">
                    {formatShiftSchedule(shift.day_time_deduction).map((schedule, index) => (
                      <span key={index} className="text-sm">
                        {schedule}
                      </span>
                    ))}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge 
                  status={shift.is_active ? EmployeeStatus.ACTIVE : EmployeeStatus.INACTIVE}
                  className="text-xs font-medium"
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => onEditShift(shift)}
                    className="h-8 w-8 hover:bg-primary/10"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {shift.is_active && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShiftToRemove(shift.id)}
                      className="h-8 w-8 hover:bg-amber-500/10 text-amber-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShiftToDelete(shift.id)}
                    className="h-8 w-8 hover:bg-destructive/10 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Remove Shift Dialog */}
      <AlertDialog open={!!shiftToRemove} onOpenChange={() => setShiftToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Shift</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the shift as inactive. The shift will no longer be available for new assignments, but existing assignments will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (shiftToRemove) {
                  onRemoveShift(shiftToRemove);
                  setShiftToRemove(null);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Shift Dialog */}
      <AlertDialog open={!!shiftToDelete} onOpenChange={() => setShiftToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shift</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the shift. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (shiftToDelete) {
                  onDeleteShift(shiftToDelete);
                  setShiftToDelete(null);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
